// On a phone (under 700 px, features/phone.css) the two panels are sheets over the bottom row. This is a sheet's grab
// handle: a pill that drags the sheet closed (the sheet follows the finger from the handle; past 80 px, or a quick flick,
// it closes, else it snaps back) and, where the sheet's own header has no close, a close button. Wider screens don't show it. The handle moves the sheet itself (its `.panel`) with an inline transform during the drag and lets the
// panel's own class transition take over on release.

import { useRef, type PointerEvent } from 'react';
import { X } from 'lucide-react';
import { IconButton } from '../ui/primitives.tsx';

/** How far down a drag must go to close the sheet, px; a quick flick needs less. */
export const SHEET_CLOSE_PX = 80;
const FLICK_PX = 24;
const FLICK_MS = 250;

/** Whether a drag that ended after `dy` px in `ms` closes the sheet. */
export const dragCloses = (dy: number, ms: number): boolean => dy > SHEET_CLOSE_PX || (dy > FLICK_PX && ms < FLICK_MS);

/** `label` names the handle's ×; a sheet whose own header has a close (the inspector's) passes none and keeps the pill alone. */
export function SheetHandle({ label, onClose }: { label?: string; onClose: () => void }) {
  const drag = useRef<{ y: number; t: number; el: HTMLElement } | null>(null);
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    const el = e.currentTarget.closest<HTMLElement>('.panel');
    if (!el) return;
    drag.current = { y: e.clientY, t: performance.now(), el };
    el.style.transition = 'none';
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    d.el.style.transform = `translateY(${Math.max(0, e.clientY - d.y)}px)`;
  };
  const end = (e: PointerEvent<HTMLDivElement>, cancelled = false) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    const dy = Math.max(0, e.clientY - d.y);
    const closes = !cancelled && dragCloses(dy, performance.now() - d.t);
    if (closes) onClose();
    // Next frame the panel has its class (collapsed or not); its own transition runs from where the finger left it.
    requestAnimationFrame(() => {
      d.el.style.transition = '';
      d.el.style.transform = '';
    });
  };
  return (
    <div className="sheet-handle" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={(e) => end(e)} onPointerCancel={(e) => end(e, true)}>
      <span className="sheet-grip" aria-hidden="true" />
      {label ? <IconButton size="sm" icon={X} label={label} onClick={onClose} /> : null}
    </div>
  );
}
